import React from "react";

/* Subtle Indian-art-inspired SVG ornaments. All currentColor so they inherit theme tones.
   Use sparingly as small decorative imprints. */

export const Mandala = ({ size = 120, className = "", strokeWidth = 0.7 }) => (
  <svg
    viewBox="0 0 120 120"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    aria-hidden="true"
    className={className}
  >
    <g transform="translate(60 60)">
      <circle r="2.2" fill="currentColor" />
      <circle r="8" />
      <circle r="14" strokeDasharray="2 2" />
      <circle r="22" />
      <circle r="34" strokeDasharray="1 3" />
      <circle r="46" />
      <circle r="54" strokeDasharray="3 3" />
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 16;
        const x1 = Math.cos(a) * 22;
        const y1 = Math.sin(a) * 22;
        const x2 = Math.cos(a) * 46;
        const y2 = Math.sin(a) * 46;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 8;
        const cx = Math.cos(a) * 30;
        const cy = Math.sin(a) * 30;
        return (
          <g key={`p-${i}`} transform={`translate(${cx} ${cy}) rotate(${(a * 180) / Math.PI})`}>
            <path d="M0 -6 C 5 -3, 5 3, 0 6 C -5 3, -5 -3, 0 -6 Z" />
          </g>
        );
      })}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 12 + Math.PI / 12;
        const cx = Math.cos(a) * 50;
        const cy = Math.sin(a) * 50;
        return <circle key={`d-${i}`} cx={cx} cy={cy} r="1" fill="currentColor" />;
      })}
    </g>
  </svg>
);

export const Paisley = ({ size = 80, className = "", strokeWidth = 0.7 }) => (
  <svg
    viewBox="0 0 80 80"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    aria-hidden="true"
    className={className}
  >
    <path d="M14 64 C 6 44, 22 12, 50 16 C 70 18, 72 40, 56 50 C 44 58, 30 56, 28 44 C 26 32, 38 26, 46 30" />
    <path d="M22 56 C 18 44, 28 24, 46 26 C 58 28, 60 40, 50 46 C 42 50, 34 48, 34 40" />
    <circle cx="42" cy="38" r="2" fill="currentColor" />
    <circle cx="50" cy="32" r="1" fill="currentColor" />
    <circle cx="36" cy="46" r="1" fill="currentColor" />
  </svg>
);

export const Lotus = ({ size = 90, className = "", strokeWidth = 0.7 }) => (
  <svg
    viewBox="0 0 90 90"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    aria-hidden="true"
    className={className}
  >
    <g transform="translate(45 70)">
      {[-60, -30, 0, 30, 60].map((rot, i) => (
        <path
          key={i}
          transform={`rotate(${rot})`}
          d="M0 0 C -10 -22, -4 -42, 0 -50 C 4 -42, 10 -22, 0 0 Z"
        />
      ))}
      <path d="M-32 0 C -20 6, 20 6, 32 0" />
      <circle cx="0" cy="-2" r="1.5" fill="currentColor" />
    </g>
  </svg>
);

export const Jaali = ({ size = 160, className = "", opacity = 0.5 }) => (
  <svg
    viewBox="0 0 160 160"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="0.6"
    aria-hidden="true"
    className={className}
    style={{ opacity }}
  >
    <defs>
      <pattern id="jaali-tile" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M0 20 Q 20 0 40 20 Q 20 40 0 20 Z" />
        <path d="M20 0 Q 40 20 20 40 Q 0 20 20 0 Z" />
        <circle cx="20" cy="20" r="1" fill="currentColor" />
      </pattern>
    </defs>
    <rect width="160" height="160" fill="url(#jaali-tile)" />
  </svg>
);

export const BorderOrnament = ({ width = 260, className = "", strokeWidth = 0.7 }) => (
  <svg
    viewBox="0 0 260 28"
    width={width}
    height={(width / 260) * 28}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    aria-hidden="true"
    className={className}
  >
    <line x1="0" y1="14" x2="60" y2="14" />
    <line x1="200" y1="14" x2="260" y2="14" />
    <g transform="translate(130 14)">
      <circle r="3" fill="currentColor" />
      <circle r="7" />
      <circle r="11" strokeDasharray="2 2" />
      <path d="M-30 0 C -22 -6, -14 -6, -7 0" />
      <path d="M30 0 C 22 -6, 14 -6, 7 0" />
      <path d="M-30 0 C -22 6, -14 6, -7 0" />
      <path d="M30 0 C 22 6, 14 6, 7 0" />
      <circle cx="-38" cy="0" r="1.2" fill="currentColor" />
      <circle cx="38" cy="0" r="1.2" fill="currentColor" />
      <circle cx="-50" cy="0" r="0.8" fill="currentColor" />
      <circle cx="50" cy="0" r="0.8" fill="currentColor" />
    </g>
  </svg>
);

export const CornerOrnament = ({ size = 110, className = "", strokeWidth = 0.7, rotate = 0 }) => (
  <svg
    viewBox="0 0 110 110"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    aria-hidden="true"
    className={className}
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <path d="M4 60 C 4 28, 28 4, 60 4" />
    <path d="M14 60 C 14 34, 34 14, 60 14" strokeDasharray="2 2" />
    <path d="M24 60 C 24 40, 40 24, 60 24" />
    <circle cx="60" cy="60" r="1.5" fill="currentColor" />
    <circle cx="40" cy="40" r="1" fill="currentColor" />
    <path d="M30 60 C 30 42, 42 30, 60 30 L 60 38 C 46 38, 38 46, 38 60 Z" />
    <path d="M60 4 L 64 8 L 60 12 L 56 8 Z" />
    <path d="M4 60 L 8 64 L 12 60 L 8 56 Z" />
  </svg>
);

export const DiyaDots = ({ count = 7, className = "", gap = 14 }) => (
  <div className={`inline-flex items-center ${className}`} style={{ gap }}>
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className="inline-block w-[3px] h-[3px] rounded-full bg-current" style={{ opacity: i % 2 ? 0.45 : 0.9 }} />
    ))}
  </div>
);
